"use client"

import { DiscordIcon } from "@/app/_components/DiscordIcon"
import { signIn, getProviders } from "next-auth/react"
import Link from "next/link"
import { useSearchParams } from "next/navigation"
import { useState, useEffect } from "react"
import { trackEvent, trackEventBeforeNav } from "@/lib/analytics/counterscale"

interface Provider {
  id: string
  name: string
  type: string
  signinUrl: string
  callbackUrl: string
}

interface SignInFormProps {
  providers?: Record<string, Provider> | null
}

function GoogleIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  )
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '11px 14px',
  background: 'var(--card-bg)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
  color: 'var(--fg-1)',
  outline: 'none',
  transition: 'border-color .15s, box-shadow .15s',
  boxShadow: 'var(--card-stack-crisp, none)',
}

const primaryBtn: React.CSSProperties = {
  width: '100%',
  color: '#fff',
  border: 0,
  borderRadius: 8,
  padding: '12px 16px',
  fontFamily: 'var(--font-mono)',
  fontWeight: 600,
  fontSize: 12.5,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  transition: 'background .15s',
}

const oauthBtnBase: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  borderRadius: 8,
  padding: '10px 14px',
  fontFamily: 'var(--font-sans)',
  fontWeight: 500,
  fontSize: 13,
  cursor: 'pointer',
  transition: 'background .15s, border-color .15s',
}

export default function SignInForm({ providers: initialProviders }: SignInFormProps) {
  const [providers, setProviders] = useState<Record<string, Provider> | null>(initialProviders || null)
  const [email, setEmail] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState("")
  const searchParams = useSearchParams()
  const callbackUrl = searchParams?.get("callbackUrl") || "/"

  useEffect(() => {
    if (!providers) {
      getProviders().then(setProviders)
    }
  }, [providers])

  // Funnel instrumentation: screen view once, plus any OAuth error the redirect
  // bounced back with (?error=<code> is a bounded NextAuth enum → low-cardinality).
  useEffect(() => {
    trackEvent("signin_view")
    const err = searchParams?.get("error")
    if (err) {
      const code = err.toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 24)
      trackEvent(`signin_error_${code || "unknown"}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setMessage("")
    trackEvent("signin_email")

    try {
      const result = await signIn("nodemailer", {
        email,
        redirect: false,
        callbackUrl,
      })

      if (result?.ok) {
        trackEvent("signin_email_sent")
        setMessage("Check your email for a sign-in link.")
      } else {
        trackEvent("signin_email_fail")
        setMessage("Something went wrong. Please try again.")
      }
    } catch (error) {
      trackEvent("signin_email_fail")
      setMessage("Something went wrong. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const hasGoogle = providers && "google" in providers
  const hasDiscord = providers && "discord" in providers
  const hasOAuth = hasGoogle || hasDiscord
  const sentMessage = message.toLowerCase().includes("check your email")

  return (
    <div>
      <form className="mo-signin-form" onSubmit={handleEmailSignIn} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label
          htmlFor="email"
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'var(--fg-3)',
            fontWeight: 600,
          }}
        >
          Email
        </label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          style={inputStyle}
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isLoading}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = 'var(--coral-deep)'
            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(181, 97, 71, 0.18)'
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = 'var(--border)'
            e.currentTarget.style.boxShadow = 'none'
          }}
        />

        <button
          type="submit"
          disabled={isLoading}
          className="mo-bg-coral-deep"
          style={{
            ...primaryBtn,
            marginTop: 4,
            opacity: isLoading ? 0.55 : 1,
            cursor: isLoading ? 'not-allowed' : 'pointer',
          }}
        >
          {isLoading ? "Sending…" : "Send sign-in link →"}
        </button>

        {message && (
          <div
            role="status"
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
              color: sentMessage ? 'var(--coral-deep)' : '#b53939',
              background: sentMessage ? 'rgba(181, 97, 71, 0.08)' : 'rgba(181, 57, 57, 0.06)',
              border: `1px solid ${sentMessage ? 'rgba(181, 97, 71, 0.22)' : 'rgba(181, 57, 57, 0.18)'}`,
              borderRadius: 6,
              padding: '8px 12px',
              textAlign: 'center',
              marginTop: 4,
            }}
          >
            {message}
          </div>
        )}
      </form>

      {hasOAuth && (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              margin: '22px 0 18px',
            }}
          >
            <div className="mo-rule" style={{ flex: 1 }} />
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10.5,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: 'var(--fg-3)',
                fontWeight: 600,
              }}
            >
              or
            </span>
            <div className="mo-rule" style={{ flex: 1 }} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: hasGoogle && hasDiscord ? '1fr 1fr' : '1fr', gap: 10 }}>
            {hasGoogle && (
              <button
                type="button"
                onClick={async () => {
                  await trackEventBeforeNav("signin_google")
                  signIn("google", { callbackUrl })
                }}
                style={{
                  ...oauthBtnBase,
                  /* Google brand button stays white in both themes per
                     Google brand guidelines; the text color is locked to
                     a dark slate so it stays readable when --fg-1 flips
                     to near-white in dark mode (would otherwise render
                     white-on-white). */
                  background: '#fff',
                  border: '1px solid var(--border)',
                  color: '#1f2937',
                }}
              >
                <GoogleIcon />
                <span>Google</span>
              </button>
            )}
            {hasDiscord && (
              <button
                type="button"
                onClick={async () => {
                  await trackEventBeforeNav("signin_discord")
                  signIn("discord", { callbackUrl })
                }}
                style={{
                  ...oauthBtnBase,
                  background: '#5865F2',
                  border: '1px solid #5865F2',
                  color: '#fff',
                }}
              >
                <DiscordIcon />
                <span>Discord</span>
              </button>
            )}
          </div>
        </>
      )}

      {/* Any other providers (e.g. credentials in dev) */}
      {providers && Object.values(providers).filter(p => p.id !== "nodemailer" && p.id !== "credentials" && p.id !== "discord" && p.id !== "google").map((provider) => (
        <div key={provider.name} style={{ marginTop: 12 }}>
          <button
            onClick={async () => {
              await trackEventBeforeNav("signin_other")
              signIn(provider.id, { callbackUrl })
            }}
            className="mo-bg-card-soft"
            style={{
              ...oauthBtnBase,
              width: '100%',
              border: '1px solid var(--border)',
              color: 'var(--fg-2)',
            }}
          >
            Sign in with {provider.name}
          </button>
        </div>
      ))}

      <p
        style={{
          margin: '20px 0 0',
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          lineHeight: 1.5,
          letterSpacing: '0.02em',
          color: 'var(--fg-3)',
          textAlign: 'center',
        }}
      >
        By signing in you agree to the{' '}
        <Link href="/privacy" style={{ color: 'var(--coral-deep)' }}>
          privacy policy
        </Link>
        .
      </p>
    </div>
  )
}
